import { createTheme } from '@mui/material';

export type PortalBranding = {
  source: 'workspace' | 'default';
  workspaceId?: string;
  displayName: string;
  tagline: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  logoUrl: string | null;
  portalIconUrl: string | null;
  appName: string;
  shortName: string;
};

export const defaultPortalBranding: PortalBranding = {
  source: 'default',
  displayName: 'CourtCaptain',
  tagline: 'Manage Your Team. Win More Matches.',
  primaryColor: '#1b5e20',
  secondaryColor: '#f4f7f4',
  accentColor: '#1565c0',
  logoUrl: null,
  portalIconUrl: null,
  appName: 'CourtCaptain',
  shortName: 'CourtCaptain',
};

function validHexColor(value: unknown, fallback: string): string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
}

function textOrFallback(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function nullableUrl(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

export function normalizePortalBranding(value: unknown): PortalBranding {
  if (!value || typeof value !== 'object') return defaultPortalBranding;
  const source = value as Partial<PortalBranding>;
  return {
    source: source.source === 'workspace' ? 'workspace' : 'default',
    workspaceId: typeof source.workspaceId === 'string' ? source.workspaceId : undefined,
    displayName: textOrFallback(source.displayName, defaultPortalBranding.displayName),
    tagline: typeof source.tagline === 'string' && source.tagline.trim() ? source.tagline.trim() : null,
    primaryColor: validHexColor(source.primaryColor, defaultPortalBranding.primaryColor),
    secondaryColor: validHexColor(source.secondaryColor, defaultPortalBranding.secondaryColor),
    accentColor: validHexColor(source.accentColor, defaultPortalBranding.accentColor),
    logoUrl: nullableUrl(source.logoUrl),
    portalIconUrl: nullableUrl(source.portalIconUrl),
    appName: textOrFallback(source.appName, textOrFallback(source.displayName, defaultPortalBranding.appName)),
    shortName: textOrFallback(source.shortName, textOrFallback(source.displayName, defaultPortalBranding.shortName)),
  };
}

export function createPortalTheme(branding: PortalBranding) {
  return createTheme({
    palette: {
      mode: 'light',
      primary: { main: branding.primaryColor },
      secondary: { main: branding.accentColor },
      background: { default: branding.secondaryColor },
    },
    shape: { borderRadius: 12 },
    typography: { fontFamily: ['Inter', 'Roboto', 'Arial', 'sans-serif'].join(',') },
  });
}
